import React, { useState, useMemo } from 'react';
import './AnalysisTab.css';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  ComposedChart,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { format, parseISO, isWithinInterval, getDay } from 'date-fns';
import * as XLSX from 'xlsx';

const AnalysisTab = ({ data, settings }) => {
  const [startDate, setStartDate] = useState(() => {
    if (data.length > 0) {
      const dates = data.map(d => new Date(d.date));
      return format(new Date(Math.min(...dates)), 'yyyy-MM-dd');
    }
    return format(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000), 'yyyy-MM-dd');
  });
  
  const [endDate, setEndDate] = useState(() => {
    if (data.length > 0) {
      const dates = data.map(d => new Date(d.date));
      return format(new Date(Math.max(...dates)), 'yyyy-MM-dd');
    }
    return format(new Date(), 'yyyy-MM-dd');
  });
  
  const [filterGroup, setFilterGroup] = useState('All');
  const [filterTimeGroup, setFilterTimeGroup] = useState('All');
  const [showGraphs, setShowGraphs] = useState(false);
  const [showAdditionalGraphs, setShowAdditionalGraphs] = useState(false);

  const filteredData = useMemo(() => {
    if (!data || data.length === 0) return [];
    
    let filtered = data.filter(entry => {
      const entryDate = new Date(entry.date);
      const start = parseISO(startDate);
      const end = parseISO(endDate);
      return isWithinInterval(entryDate, { start, end });
    });

    if (filterGroup !== 'All') {
      filtered = filtered.filter(entry => entry.group === filterGroup);
    }

    if (filterTimeGroup !== 'All') {
      filtered = filtered.filter(entry => entry.time_group === filterTimeGroup);
    }

    return filtered;
  }, [data, startDate, endDate, filterGroup, filterTimeGroup]);

  const chartData = useMemo(() => {
    const grouped = {};
    
    filteredData.forEach(entry => {
      const dateKey = format(new Date(entry.date), 'yyyy-MM-dd');
      if (!grouped[dateKey]) {
        grouped[dateKey] = {
          date: dateKey,
          total_calculated_formula: 0,
          total_journal: 0,
          difference: 0,
          daily_revenue: 0,
          count: 0
        };
      }
      grouped[dateKey].total_calculated_formula += entry.total_calculated_formula || 0;
      grouped[dateKey].total_journal += entry.total_journal || 0;
      grouped[dateKey].difference += entry.difference || 0;
      grouped[dateKey].daily_revenue += entry.daily_revenue || 0;
      grouped[dateKey].count += 1;
    });

    return Object.values(grouped)
      .map(item => ({
        ...item,
        difference: item.difference / item.count
      }))
      .sort((a, b) => a.date.localeCompare(b.date));
  }, [filteredData]);

  const groupRevenueData = useMemo(() => {
    const grouped = {};
    filteredData.forEach(entry => {
      if (!grouped[entry.group]) {
        grouped[entry.group] = 0;
      }
      grouped[entry.group] += entry.daily_revenue || 0;
    });
    return Object.entries(grouped).map(([name, value]) => ({ name, value }));
  }, [filteredData]);

  const dayOfWeekRevenueData = useMemo(() => {
    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const dayOrder = [1, 2, 3, 4, 5, 6, 0]; // Monday to Sunday order
    const dayRevenue = {
      'Sunday': 0,
      'Monday': 0,
      'Tuesday': 0,
      'Wednesday': 0,
      'Thursday': 0,
      'Friday': 0,
      'Saturday': 0
    };

    filteredData.forEach(entry => {
      const entryDate = new Date(entry.date);
      const dayIndex = getDay(entryDate); // 0 = Sunday, 1 = Monday, etc.
      const dayName = dayNames[dayIndex];
      dayRevenue[dayName] += entry.daily_revenue || 0;
    });

    // Return in Monday-Sunday order for better visualization
    return dayOrder.map(dayIndex => ({
      day: dayNames[dayIndex],
      revenue: dayRevenue[dayNames[dayIndex]]
    }));
  }, [filteredData]);

  const COLORS = ['#667eea', '#764ba2', '#f093fb', '#4facfe', '#00f2fe'];

  const exportReport = () => {
    if (filteredData.length === 0) {
      alert('No data to export');
      return;
    }

    const ws = XLSX.utils.json_to_sheet(filteredData.map(entry => ({
      Date: format(new Date(entry.date), 'yyyy-MM-dd'),
      Group: entry.group,
      'Time Group': entry.time_group,
      'Total Calculated': entry.total_calculated,
      'Font Caisse': entry.font_caisse,
      'Total Credit': entry.total_credit,
      'Total Achat': entry.total_achat,
      'Total Journal': entry.total_journal,
      'Total Calculated Formula': entry.total_calculated_formula,
      'Difference': entry.difference,
      'Daily Revenue': entry.daily_revenue,
      Notes: entry.notes || ''
    })));

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Revenue Data');
    
    const filename = `coffee_shop_report_${format(new Date(), 'yyyyMMdd_HHmmss')}.xlsx`;
    XLSX.writeFile(wb, filename);
    alert(`Report exported to ${filename}`);
  };

  const setDateRangeToData = () => {
    if (data.length > 0) {
      const dates = data.map(d => new Date(d.date));
      setStartDate(format(new Date(Math.min(...dates)), 'yyyy-MM-dd'));
      setEndDate(format(new Date(Math.max(...dates)), 'yyyy-MM-dd'));
    }
  };

  // Auto-generate graphs when filters change (if graphs are already shown)
  React.useEffect(() => {
    if (showGraphs && filteredData.length > 0) {
      // Graphs will automatically update due to useMemo dependencies
    }
  }, [startDate, endDate, filterGroup, filterTimeGroup, showGraphs, filteredData.length]);

  return (
    <div className="analysis-tab">
      <h2>Revenue Analysis & Graphs</h2>

      <div className="filters-panel">
        <div className="filter-group">
          <label>From:</label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
        </div>

        <div className="filter-group">
          <label>To:</label>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </div>

        <div className="filter-group">
          <label>Group:</label>
          <select
            value={filterGroup}
            onChange={(e) => setFilterGroup(e.target.value)}
          >
            <option value="All">All</option>
            {settings.groups.map(group => (
              <option key={group} value={group}>{group}</option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label>Time Group:</label>
          <select
            value={filterTimeGroup}
            onChange={(e) => setFilterTimeGroup(e.target.value)}
          >
            <option value="All">All</option>
            <option value="7am to 2pm">7am to 2pm</option>
            <option value="2pm to 10pm">2pm to 10pm</option>
          </select>
        </div>

        <button onClick={setDateRangeToData} className="btn btn-secondary">
          Set to Data Range
        </button>

        <button 
          onClick={() => {
            if (filteredData.length === 0) {
              alert('No data available for selected filters');
              return;
            }
            setShowGraphs(true);
          }} 
          className="btn btn-success"
        >
          Generate Graphs
        </button>

        <button onClick={exportReport} className="btn btn-primary">
          Export Report
        </button>
      </div>

      {filteredData.length === 0 ? (
        <div className="no-data-message">
          No data available for selected filters
        </div>
      ) : !showGraphs ? (
        <div className="generate-prompt">
          <p>Click "Generate Graphs" to view the analysis charts</p>
        </div>
      ) : (
        <div className="charts-container">
          <div className="chart-card">
            <h3>Daily Revenue Trend</h3>
            <ResponsiveContainer width="100%" height={350}>
              <LineChart data={chartData} margin={{ top: 5, right: 20, left: 15, bottom: 15 }}>
                <CartesianGrid strokeDasharray="4 4" stroke="#e0e0e0" />
                <XAxis 
                  dataKey="date" 
                  tick={{ fontSize: 12, fontWeight: 600, fill: '#555' }}
                  label={{ value: 'Date', position: 'insideBottom', offset: -10, style: { fontSize: 14, fontWeight: 700 } }}
                  angle={-45}
                  textAnchor="end"
                  height={80}
                />
                <YAxis 
                  tick={{ fontSize: 12, fontWeight: 600, fill: '#555' }}
                  label={{ value: `Revenue (${settings.currency})`, angle: -90, position: 'insideLeft', style: { fontSize: 14, fontWeight: 700 } }}
                  tickFormatter={(value) => `${value.toLocaleString()}`}
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'rgba(255, 255, 255, 0.98)',
                    border: '2px solid #ff6b6b',
                    borderRadius: '12px',
                    padding: '12px 16px',
                    boxShadow: '0 8px 25px rgba(0, 0, 0, 0.2)'
                  }}
                  labelStyle={{ fontSize: 14, fontWeight: 700, marginBottom: 8, paddingBottom: 8, borderBottom: '2px solid #f0f0f0' }}
                  itemStyle={{ fontSize: 13, fontWeight: 600 }}
                  formatter={(value) => [`${value.toLocaleString()}${settings.currency}`, 'Daily Revenue']}
                />
                <Legend 
                  wrapperStyle={{ paddingTop: '20px' }}
                  iconType="line"
                />
                <Line
                  type="monotone"
                  dataKey="daily_revenue"
                  stroke="#667eea"
                  strokeWidth={3}
                  name="Daily Revenue"
                  dot={{ fill: '#667eea', r: 5, strokeWidth: 2, stroke: '#fff' }}
                  activeDot={{ r: 7, strokeWidth: 2 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>

          <div className="chart-card">
            <h3>Total Calculated vs Journal de Ventes</h3>
            <ResponsiveContainer width="100%" height={350}>
              <LineChart data={chartData} margin={{ top: 5, right: 20, left: 15, bottom: 15 }}>
                <CartesianGrid strokeDasharray="4 4" stroke="#e0e0e0" />
                <XAxis 
                  dataKey="date" 
                  tick={{ fontSize: 12, fontWeight: 600, fill: '#555' }}
                  label={{ value: 'Date', position: 'insideBottom', offset: -10, style: { fontSize: 14, fontWeight: 700 } }}
                  angle={-45}
                  textAnchor="end"
                  height={80}
                />
                <YAxis 
                  tick={{ fontSize: 12, fontWeight: 600, fill: '#555' }}
                  label={{ value: `Amount (${settings.currency})`, angle: -90, position: 'insideLeft', style: { fontSize: 14, fontWeight: 700 } }}
                  tickFormatter={(value) => `${value.toLocaleString()}`}
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'rgba(255, 255, 255, 0.98)',
                    border: '2px solid #ff6b6b',
                    borderRadius: '12px',
                    padding: '12px 16px',
                    boxShadow: '0 8px 25px rgba(0, 0, 0, 0.2)'
                  }}
                  labelStyle={{ fontSize: 14, fontWeight: 700, marginBottom: 8, paddingBottom: 8, borderBottom: '2px solid #f0f0f0' }}
                  itemStyle={{ fontSize: 13, fontWeight: 600 }}
                  formatter={(value) => [`${value.toLocaleString()}${settings.currency}`, '']}
                />
                <Legend 
                  wrapperStyle={{ paddingTop: '20px' }}
                  iconType="line"
                />
                <Line
                  type="monotone"
                  dataKey="total_calculated_formula"
                  stroke="#06A77D"
                  strokeWidth={3}
                  name="Total Calculated Formula"
                  dot={{ fill: '#06A77D', r: 5, strokeWidth: 2, stroke: '#fff' }}
                  activeDot={{ r: 7, strokeWidth: 2 }}
                />
                <Line
                  type="monotone"
                  dataKey="total_journal"
                  stroke="#F77F00"
                  strokeWidth={3}
                  name="Total Journal de Ventes"
                  dot={{ fill: '#F77F00', r: 5, strokeWidth: 2, stroke: '#fff' }}
                  activeDot={{ r: 7, strokeWidth: 2 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>

          <div className="chart-card">
            <h3>Difference Trend (Calculated - Journal)</h3>
            <ResponsiveContainer width="100%" height={350}>
              <LineChart data={chartData} margin={{ top: 5, right: 20, left: 15, bottom: 15 }}>
                <CartesianGrid strokeDasharray="4 4" stroke="#e0e0e0" />
                <XAxis 
                  dataKey="date" 
                  tick={{ fontSize: 12, fontWeight: 600, fill: '#555' }}
                  label={{ value: 'Date', position: 'insideBottom', offset: -10, style: { fontSize: 14, fontWeight: 700 } }}
                  angle={-45}
                  textAnchor="end"
                  height={80}
                />
                <YAxis 
                  tick={{ fontSize: 12, fontWeight: 600, fill: '#555' }}
                  label={{ value: `Difference (${settings.currency})`, angle: -90, position: 'insideLeft', style: { fontSize: 14, fontWeight: 700 } }}
                  tickFormatter={(value) => `${value.toLocaleString()}`}
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'rgba(255, 255, 255, 0.98)',
                    border: '2px solid #ff6b6b',
                    borderRadius: '12px',
                    padding: '12px 16px',
                    boxShadow: '0 8px 25px rgba(0, 0, 0, 0.2)'
                  }}
                  labelStyle={{ fontSize: 14, fontWeight: 700, marginBottom: 8, paddingBottom: 8, borderBottom: '2px solid #f0f0f0' }}
                  itemStyle={{ fontSize: 13, fontWeight: 600 }}
                  formatter={(value) => [`${value.toLocaleString()}${settings.currency}`, 'Difference']}
                />
                <Legend 
                  wrapperStyle={{ paddingTop: '20px' }}
                  iconType="line"
                />
                <Line
                  type="monotone"
                  dataKey="difference"
                  stroke="#D62828"
                  strokeWidth={3}
                  name="Difference"
                  dot={{ fill: '#D62828', r: 5, strokeWidth: 2, stroke: '#fff' }}
                  activeDot={{ r: 7, strokeWidth: 2 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {groupRevenueData.length > 1 && (
            <div className="chart-card">
              <h3>Revenue Distribution by Group</h3>
              <ResponsiveContainer width="100%" height={350}>
                <PieChart>
                  <Pie
                    data={groupRevenueData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent, value }) => `${name}\n${(percent * 100).toFixed(1)}%\n${value.toLocaleString()}${settings.currency}`}
                    outerRadius={120}
                    fill="#8884d8"
                    dataKey="value"
                    paddingAngle={3}
                  >
                    {groupRevenueData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} stroke="#fff" strokeWidth={2} />
                    ))}
                  </Pie>
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'rgba(255, 255, 255, 0.98)',
                      border: '2px solid #ff6b6b',
                      borderRadius: '12px',
                      padding: '12px 16px',
                      boxShadow: '0 8px 25px rgba(0, 0, 0, 0.2)'
                    }}
                    labelStyle={{ fontSize: 14, fontWeight: 700, marginBottom: 8, paddingBottom: 8, borderBottom: '2px solid #f0f0f0' }}
                    itemStyle={{ fontSize: 13, fontWeight: 600 }}
                    formatter={(value) => [`${value.toLocaleString()}${settings.currency}`, 'Revenue']}
                  />
                  <Legend 
                    wrapperStyle={{ paddingTop: '20px' }}
                    iconType="circle"
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}

          {showAdditionalGraphs && (
            <>
              <div className="chart-card">
                <h3>Daily Components Breakdown</h3>
            <ResponsiveContainer width="100%" height={350}>
              <BarChart data={chartData} margin={{ top: 5, right: 20, left: 15, bottom: 15 }}>
                <CartesianGrid strokeDasharray="4 4" stroke="#e0e0e0" />
                <XAxis 
                  dataKey="date" 
                  tick={{ fontSize: 12, fontWeight: 600, fill: '#555' }}
                  label={{ value: 'Date', position: 'insideBottom', offset: -10, style: { fontSize: 14, fontWeight: 700 } }}
                  angle={-45}
                  textAnchor="end"
                  height={80}
                />
                <YAxis 
                  tick={{ fontSize: 12, fontWeight: 600, fill: '#555' }}
                  label={{ value: `Amount (${settings.currency})`, angle: -90, position: 'insideLeft', style: { fontSize: 14, fontWeight: 700 } }}
                  tickFormatter={(value) => `${value.toLocaleString()}`}
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'rgba(255, 255, 255, 0.98)',
                    border: '2px solid #ff6b6b',
                    borderRadius: '12px',
                    padding: '12px 16px',
                    boxShadow: '0 8px 25px rgba(0, 0, 0, 0.2)'
                  }}
                  labelStyle={{ fontSize: 14, fontWeight: 700, marginBottom: 8, paddingBottom: 8, borderBottom: '2px solid #f0f0f0' }}
                  itemStyle={{ fontSize: 13, fontWeight: 600 }}
                  formatter={(value) => [`${value.toLocaleString()}${settings.currency}`, '']}
                />
                <Legend 
                  wrapperStyle={{ paddingTop: '20px' }}
                  iconType="square"
                />
                <Bar dataKey="total_journal" stackId="a" fill="#06A77D" name="Journal" radius={[0, 0, 0, 0]} />
                <Bar dataKey="total_achat" stackId="a" fill="#F77F00" name="Achat" radius={[0, 0, 0, 0]} />
                <Bar dataKey="total_credit" stackId="a" fill="#2E86AB" name="Credit" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="chart-card">
            <h3>Daily Revenue by Day of Week</h3>
            <ResponsiveContainer width="100%" height={350}>
              <ComposedChart data={dayOfWeekRevenueData} margin={{ top: 5, right: 20, left: 15, bottom: 15 }}>
                <CartesianGrid strokeDasharray="4 4" stroke="#e0e0e0" />
                <XAxis 
                  dataKey="day" 
                  tick={{ fontSize: 13, fontWeight: 600, fill: '#555' }}
                  label={{ value: 'Day of Week', position: 'insideBottom', offset: -10, style: { fontSize: 14, fontWeight: 700 } }}
                />
                <YAxis 
                  tick={{ fontSize: 12, fontWeight: 600, fill: '#555' }}
                  label={{ value: `Revenue (${settings.currency})`, angle: -90, position: 'insideLeft', style: { fontSize: 14, fontWeight: 700 } }}
                  tickFormatter={(value) => `${value.toLocaleString()}`}
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'rgba(255, 255, 255, 0.98)',
                    border: '2px solid #ff6b6b',
                    borderRadius: '12px',
                    padding: '12px 16px',
                    boxShadow: '0 8px 25px rgba(0, 0, 0, 0.2)'
                  }}
                  labelStyle={{ fontSize: 14, fontWeight: 700, marginBottom: 8, paddingBottom: 8, borderBottom: '2px solid #f0f0f0' }}
                  itemStyle={{ fontSize: 13, fontWeight: 600 }}
                  formatter={(value) => [`${value.toLocaleString()}${settings.currency}`, '']}
                />
                <Legend 
                  wrapperStyle={{ paddingTop: '20px' }}
                  iconType="square"
                />
                <Bar 
                  dataKey="revenue" 
                  fill="#667eea" 
                  name="Daily Revenue (Bar)"
                  radius={[8, 8, 0, 0]}
                />
                <Line
                  type="monotone"
                  dataKey="revenue"
                  stroke="#ff6b6b"
                  strokeWidth={3}
                  name="Daily Revenue (Line)"
                  dot={{ fill: '#ff6b6b', r: 5, strokeWidth: 2, stroke: '#fff' }}
                  activeDot={{ r: 7, strokeWidth: 2 }}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
            </>
          )}

          <div style={{ gridColumn: '1 / -1', textAlign: 'center', marginTop: '20px' }}>
            <button 
              onClick={() => setShowAdditionalGraphs(!showAdditionalGraphs)} 
              className="btn btn-secondary"
            >
              {showAdditionalGraphs ? 'Hide Additional Graphs' : 'Show Additional Graphs'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AnalysisTab;

